import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import env from '../env';
import { Path } from '../Routing';
import useIncorrectPlatformFallback from './hooks/useIncorrectPlatformFallback';

const Page = styled.main`
  box-sizing: border-box;
  min-height: 100dvh;
  width: 100%;
  overflow-x: hidden;
  padding: 40px 24px 56px;
  background: #ffffff;
  color: #202124;

  @media (min-width: 600px) {
    padding: 56px 48px 72px;
  }

  @media (max-width: 359px) {
    padding-right: 16px;
    padding-bottom: 40px;
    padding-left: 16px;
  }
`;

const Content = styled.div`
  width: 100%;
  margin: 0 auto;
`;

const Intro = styled.header`
  position: relative;
  left: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: calc(100vw + 2px);
  width: calc(100dvw + 2px);
  box-sizing: border-box;
  isolation: isolate;
  margin-top: -40px;
  padding: 65px 30px 52px;
  transform: translateX(-50%);
  text-align: center;

  &::before {
    position: absolute;
    z-index: -1;
    inset: 0 -1px;
    background: #f5f5f5;
    clip-path: url('#kindred-landing-curve');
    content: '';
  }

  @media (min-width: 600px) {
    margin-top: -56px;
    padding: 56px 48px 72px;
  }

  @media (max-width: 359px) {
    padding-right: 20px;
    padding-left: 20px;
  }
`;

const CurveDefinition = styled.svg`
  position: absolute;
  width: 0;
  height: 0;
`;

const IntroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translateY(-12px);
  margin-bottom: 35px;
  margin-top: 20px;

  @media (min-width: 600px) {
    transform: translateY(-16px);
  }
`;

const Logo = styled.img`
  width: 75px;
  height: 75px;
  object-fit: contain;

  @media (min-width: 600px) {
    width: 96px;
    height: 96px;
  }
`;

const Title = styled.h1`
  margin: 20px 0 0;
  max-width: 480px;
  color: #1b2457;
  font-family: 'Fira Sans', sans-serif;
  font-size: 24px;
  font-weight: 400;
  font-style: normal;
  line-height: 100%;
  letter-spacing: 0;
  text-align: center;

  @media (min-width: 600px) {
    max-width: 650px;
    font-size: 30px;
    line-height: 105%;
  }

  @media (max-width: 375px) {
    font-size: 21px;
  }

  strong {
    font-weight: 700;
  }
`;

const PrivacyNotice = styled.p`
  display: block;
  width: min(100%, 560px);
  box-sizing: border-box;
  margin: -25px auto 0;
  max-width: 560px;
  padding: 0 8px;
  color: #1b2457;
  font-family: 'Fira Sans', sans-serif;
  font-size: 18px;
  font-weight: 400;
  font-style: normal;
  line-height: 100%;
  letter-spacing: 0;
  text-align: center !important;

  @media (min-width: 600px) {
    font-size: 22px;
    line-height: 120%;
  }

  @media (max-width: 375px) {
    font-size: 16px;
  }
`;

const PrivacyLink = styled.a`
  color: inherit;
  text-decoration: underline;
`;

const Step = styled.section`
  margin-top: 40px;

  @media (min-width: 600px) {
    margin-top: 56px;
  }

  @media (max-width: 359px) {
    margin-top: 32px;
  }
`;

const StepHeader = styled.h2`
  margin: 0 0 18px;
  font-size: 20px;
  line-height: 1.3;
  font-weight: 700;
  color: #1b2457;

  @media (min-width: 600px) {
    margin-bottom: 24px;
    font-size: 26px;
  }

  @media (max-width: 375px) {
    font-size: 18px;
  }
`;

const Carousel = styled.div`
  --edge-space: 24px;
  --image-width: calc(100vw - 60px);
  --slide-gap: 10px;

  position: relative;
  left: 50%;
  width: 100vw;
  transform: translateX(-50%);
  overflow: hidden;

  @media (max-width: 359px) {
    --edge-space: 16px;
    --image-width: calc(100vw - 44px);
    --slide-gap: 8px;
  }

  @media (min-width: 600px) {
    --edge-space: 32px;
    --image-width: calc(100vw - 96px);
    --slide-gap: 14px;
  }

  @media (min-width: 768px) {
    --edge-space: 48px;
    --image-width: min(760px, calc(100vw - 120px));
    --slide-gap: 16px;
  }
`;

const Slides = styled.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  gap: var(--slide-gap);
  overflow-x: auto;
  padding: 0 var(--edge-space);
  scroll-padding-left: var(--edge-space);
  scroll-snap-type: x mandatory;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const SlideImage = styled.img`
  display: block;
  flex: 0 0 var(--image-width);
  width: var(--image-width);
  height: auto;
  aspect-ratio: 646 / 494;
  border-radius: 16px;
  background: #f5f5f5;
  object-fit: contain;
  scroll-snap-align: start;
  scroll-snap-stop: always;
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 18px;
`;

const Dot = styled.button<{ active: boolean }>`
  position: relative;
  width: 24px;
  height: 24px;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;

  &::before {
    position: absolute;
    top: 8px;
    left: 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ active }) => (active ? '#202124' : '#d1d3d4')};
    content: '';
  }
`;

const stepOneImages = [
  require('../Assets/KindredPluginActivation/step1ImageOne.png'),
  require('../Assets/KindredPluginActivation/step1ImageTwo.png'),
  require('../Assets/KindredPluginActivation/step1ImageThree.png'),
];

const stepTwoImages = [
  require('../Assets/KindredPluginActivation/step2ImageOne.png'),
  require('../Assets/KindredPluginActivation/step2ImageTwo.png'),
  require('../Assets/KindredPluginActivation/step2ImageThree.png'),
];

interface StepCarouselProps {
  images: string[];
  label: string;
}

const StepCarousel = ({ images, label }: StepCarouselProps) => {
  const { t } = useTranslation();
  const slidesRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const goToSlide = (index: number) => {
    const slides = slidesRef.current;
    const target = slides?.children[index] as HTMLElement | undefined;
    if (!slides || !target) return;

    const leftInset = parseFloat(getComputedStyle(slides).paddingLeft);
    slides.scrollTo({
      left: target.offsetLeft - leftInset,
      behavior: 'smooth',
    });
    setActiveIndex(index);
  };

  const updateActiveSlide = () => {
    const slides = slidesRef.current;
    if (!slides) return;

    const leftInset = parseFloat(getComputedStyle(slides).paddingLeft);
    const slideElements = Array.from(slides.children) as HTMLElement[];
    const nextIndex = slideElements.reduce(
      (closestIndex, slide, index) =>
        Math.abs(slide.offsetLeft - leftInset - slides.scrollLeft) <
        Math.abs(
          slideElements[closestIndex].offsetLeft - leftInset - slides.scrollLeft
        )
          ? index
          : closestIndex,
      0
    );

    setActiveIndex(nextIndex);
  };

  useEffect(() => {
    const alignActiveSlide = () => {
      const slides = slidesRef.current;
      const target = slides?.children[activeIndex] as HTMLElement | undefined;
      if (!slides || !target) return;

      const leftInset = parseFloat(getComputedStyle(slides).paddingLeft);
      slides.scrollTo({ left: target.offsetLeft - leftInset });
    };

    window.addEventListener('resize', alignActiveSlide);
    return () => window.removeEventListener('resize', alignActiveSlide);
  }, [activeIndex]);

  return (
    <Carousel aria-label={label}>
      <Slides ref={slidesRef} onScroll={updateActiveSlide}>
        {images.map((image, index) => (
          <SlideImage
            key={image}
            src={image}
            alt={t('KindredPluginActivation.landing.imageAlt', {
              label,
              number: index + 1,
            })}
          />
        ))}
      </Slides>
      <Dots>
        {images.map((image, index) => (
          <Dot
            key={image}
            active={index === activeIndex}
            aria-label={t('KindredPluginActivation.landing.showImage', {
              label: label.toLowerCase(),
              number: index + 1,
            })}
            aria-current={index === activeIndex ? 'true' : undefined}
            onClick={() => goToSlide(index)}
          />
        ))}
      </Dots>
    </Carousel>
  );
};

const KindredActivation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useIncorrectPlatformFallback();

  useEffect(() => {
    const handlePluginPermissions = (
      message: MessageEvent<'' | { type?: string; givenPermission?: boolean }>
    ) => {
      if (message.data === '') {
        window.location.reload();
        return;
      }

      if (message.data?.type !== 'PluginEnabled') return;

      const hasFullPermission = message.data.givenPermission === true;
      localStorage.setItem('kindred_plugin_activated', 'true');
      localStorage.setItem(
        'kindred_permission_type',
        hasFullPermission ? 'full' : 'partial'
      );

      navigate({
        pathname: hasFullPermission
          ? Path.KindredPluginActivationSuccess
          : Path.KindredPluginActivationPartialPermissions,
        search: window.location.search,
      });
    };

    window.addEventListener('message', handlePluginPermissions);
    return () => window.removeEventListener('message', handlePluginPermissions);
  }, [navigate]);

  return (
    <Page>
      <Content>
        <CurveDefinition aria-hidden="true">
          <defs>
            <clipPath
              id="kindred-landing-curve"
              clipPathUnits="objectBoundingBox"
            >
              <path d="M 0 0 H 1 V 0.72 Q 0.5 1, 0 0.72 Z" />
            </clipPath>
          </defs>
        </CurveDefinition>
        <Intro>
          <IntroContent>
            <Logo
              src={require('../Assets/KindredPluginActivation/KindredLogo.png')}
              alt={t('KindredPluginActivation.landing.logoAlt')}
            />
            <Title>
              <Trans
                i18nKey="KindredPluginActivation.landing.title"
                components={{ strong: <strong /> }}
              />
            </Title>
          </IntroContent>
        </Intro>

        <PrivacyNotice>
          <Trans
            i18nKey="KindredPluginActivation.landing.privacyNotice"
            components={{
              policy: (
                <PrivacyLink
                  href={env.privacy_policy_url}
                  target="_blank"
                  rel="noreferrer"
                />
              ),
            }}
          />
        </PrivacyNotice>

        <Step>
          <StepHeader>
            {t('KindredPluginActivation.landing.step', { number: 1 })}
          </StepHeader>
          <StepCarousel
            images={stepOneImages}
            label={t('KindredPluginActivation.landing.instructions', {
              number: 1,
            })}
          />
        </Step>

        <Step>
          <StepHeader>
            {t('KindredPluginActivation.landing.step', { number: 2 })}
          </StepHeader>
          <StepCarousel
            images={stepTwoImages}
            label={t('KindredPluginActivation.landing.instructions', {
              number: 2,
            })}
          />
        </Step>
      </Content>
    </Page>
  );
};

export default KindredActivation;
